import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { Message } from '@patsnap/tahiti-core/common/message';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { feedbackApi } from 'library-@/apis/feedback';
import { FeedbackStatus } from './feedback.type';

export const FeedbackService = Symbol('FeedbackService');

export interface FeedbackService {
    status: FeedbackStatus;
    onStatusChange: Event<FeedbackStatus>;
    init: (type: string, params: { [key in string]: any }) => void;
    handleLike: () => void;
    handleDislike: (callback: () => void) => void;
    submit: (typeSuffix?: 'T' | 'F', comment?: string) => void; // T: like, F: dislike; 后端该字段只能存16个字符；
}

@injectable()
export class FeedbackServiceImpl implements FeedbackService {
    private _onStatusChangeEmitter = new Emitter<FeedbackStatus>();
    readonly onStatusChange = this._onStatusChangeEmitter.event;

    public status: FeedbackStatus = FeedbackStatus.DEFAULT;
    private _type = '';
    private _params = {}

    constructor(
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private _i18n: I18nService
    ) { }

    private get _isLike() {
        return this.status === FeedbackStatus.LIKE;
    }

    private get _isDislike() {
        return this.status === FeedbackStatus.DISLIKE;
    }

    private get _isDefault() {
        return this.status === FeedbackStatus.DEFAULT;
    }

    private _updateStatus(status: FeedbackStatus) {
        this.status = status;
        this._onStatusChangeEmitter.fire(this.status);
    }

    public init(type: string, params: { [key in string]: any }) {
        this._type = type;
        this._params = params;
    }

    public submit(typeSuffix = 'T', comment = '') {
        const type = `${this._type}-${typeSuffix}`;


        this._updateStatus(
            typeSuffix === 'T'
                ? FeedbackStatus.LIKE
                : FeedbackStatus.DISLIKE
        );

        feedbackApi({
            ...this._params,
            type,
            comment,
        });

        this._message.closeAll();
        this._message.success({
            message: this._i18n.t('feedback.thanksTips') as string,
            showClose: true,
        })
    };

    public handleLike() {
        if (this._isDislike) return;
        if (this._isLike) {
            this._updateStatus(FeedbackStatus.DEFAULT);
            return;
        }

        this.submit('T');
    }

    public handleDislike(callback: () => void) {
        if (this._isDefault) {
            callback();
        }

        if (this._isDislike) {
            this._updateStatus(FeedbackStatus.DEFAULT);
        }
    }
}